<!-- eslint-disable vue/no-v-html -->
<script setup lang="ts">
import type { Slide } from '@autobid/strapi-integration/typescript/strapi/Slides'
import { useCmsConfig } from '@autobid/strapi-integration/composable/useCmsConfig'
import { trackEvent } from '@autobid/tracking/src/utils/trackEvent'
import StrapiImage from '@autobid/ui/components/common/strapi/Image.vue'
import { useCkeditor } from '@autobid/ui/composables/useCkeditor'

interface Props {
  data: NonNullable<Slide>
  withCounterButton?: boolean
  showThumbs?: boolean
  index: number
}

const props = defineProps<Props>()

const isLazyLoading = props.index > 0

const { url: _url } = useCmsConfig()
const { formatContent } = useCkeditor()

function handleClick() {
  if (!props.data.url?.length) {
    return
  }

  trackEvent(props.data.matomo)
}
</script>

<template>
  <swiper-slide class="relative">
    <component
      :is="data.url?.length ? 'a' : 'div'"
      class="absolute inset-0 flex h-full w-full justify-center"
      v-bind="{
        ...(data.url?.length ? { href: data.url, target: '_blank' } : {}),
      }"
      @click="handleClick"
    >
      <div
        class="align-center container absolute inset-0 z-50 mx-auto flex h-full w-full min-[320px]:pl-2 lg:pl-6"
        :class="
          withCounterButton ? 'xl:justify-center xl:pl-[calc(350px+2rem)]' : ''
        "
      >
        <div
          class="content-wrapper relative z-[9999] flex h-full max-h-[100%-8px] w-full flex-col justify-center gap-4"
          :class="{
            'pb-2': showThumbs,
          }"
        >
          <div
            class="content title prose-sm !leading-[1.25] relative z-50 flex w-fit max-w-none flex-col justify-center gap-4 break-words md:prose-base lg:prose-lg xl:prose-2xl md:flex"
            v-html="data.title.trim()"
          />
          <div
            v-if="data.description"
            class="content description prose-sm relative z-50 hidden w-fit max-w-[calc(100%-48px)] flex-col gap-4 break-words md:prose-base md:flex"
            v-html="formatContent(data.description.trim())"
          />
          <div
            v-if="data.auctionDescription"
            id="auctionDescription"
            class="content description prose-sm relative z-50 flex w-max max-w-none flex-col gap-4 break-words md:prose-base"
            :class="data.auctionDescriptionAlign === 'right' ? 'ml-auto' : ''"
            v-html="formatContent(data.auctionDescription.trim())"
          />
        </div>
      </div>
      <StrapiImage
        v-if="data.slide.mime.includes('image')"
        :key="data.slide.url"
        :loading="isLazyLoading ? 'lazy' : 'eager'"
        :data="data.slide"
        class="h-full w-full object-cover object-[70%_20%] sm:object-[35%_20%] md:block md:object-[70%_20%] lg:object-[65%_20%] xl:object-center"
        :class="{
          hidden: data.slideMobile,
        }"
      />
      <StrapiImage
        v-if="data.slideMobile?.mime.includes('image')"
        :key="data.slideMobile.url"
        :loading="isLazyLoading ? 'lazy' : 'eager'"
        :data="data.slideMobile"
        class="h-full w-full object-cover object-[70%_20%] sm:object-[35%_20%] md:hidden"
      />
      <div v-if="isLazyLoading" class="swiper-lazy-preloader" />

      <video
        v-if="data.slide.mime.includes('video')"
        autoplay
        muted
        loop
        playsinline
        class="h-full w-full object-cover md:block"
        :class="{
          hidden: data.slideMobile,
        }"
      >
        <source :src="`${_url}${data.slide.url}`" :type="data.slide.mime">
      </video>
      <video
        v-if="data.slideMobile?.mime.includes('video')"
        autoplay
        loop
        muted
        playsinline
        class="h-full w-full object-cover md:hidden"
      >
        <source
          :src="`${_url}${data.slideMobile.url}`"
          :type="data.slideMobile.mime"
        >
      </video>
    </component>
  </swiper-slide>
</template>
